import React, { useState, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Tooltip,
  Slider,
  Divider,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

// ── Debounce helper ────────────────────────────────────────────────────────────

function useDebounce(fn, delay = 600) {
  const timer = useRef(null);
  return useCallback((...args) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => fn(...args), delay);
  }, [fn, delay]);
}

// ── Section wrapper ────────────────────────────────────────────────────────────

const Section = ({ title, children }) => (
  <Box sx={{ mb: 4 }}>
    <Typography
      variant="caption"
      sx={{
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: '#888',
        display: 'block',
        mb: 1.5,
      }}
    >
      {title}
    </Typography>
    {children}
  </Box>
);

// ── Color row ──────────────────────────────────────────────────────────────────

const ColorRow = ({ name, value, onChange, onDelete, deletable }) => {
  const [localVal, setLocalVal] = useState(value || '#000000');

  const handleChange = (v) => {
    setLocalVal(v);
    onChange(name, v);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
      {/* Swatch */}
      <Box
        component="label"
        sx={{
          width: 28,
          height: 28,
          borderRadius: 1,
          background: localVal,
          border: '1px solid rgba(0,0,0,0.12)',
          cursor: 'pointer',
          flexShrink: 0,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <input
          type="color"
          value={localVal}
          onChange={(e) => handleChange(e.target.value)}
          style={{
            position: 'absolute',
            opacity: 0,
            width: '100%',
            height: '100%',
            cursor: 'pointer',
            inset: 0,
          }}
        />
      </Box>
      {/* Label */}
      <Typography
        variant="body2"
        sx={{ fontSize: 12, color: '#555', width: 90, flexShrink: 0, fontFamily: 'monospace' }}
      >
        {name}
      </Typography>
      {/* Hex input */}
      <TextField
        size="small"
        value={localVal}
        onChange={(e) => handleChange(e.target.value)}
        sx={{
          flex: 1,
          '& .MuiInputBase-input': { fontSize: 12, fontFamily: 'monospace', py: 0.5 },
        }}
      />
      {deletable && (
        <Tooltip title="Remove">
          <IconButton size="small" onClick={() => onDelete(name)} sx={{ color: '#ccc', '&:hover': { color: '#e53e3e' } }}>
            <DeleteOutlineIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

// ── Color Palette section ──────────────────────────────────────────────────────

const STANDARD_COLORS = ['primary', 'secondary', 'accent', 'background', 'surface', 'text', 'muted', 'success', 'warning', 'error'];

const ColorPalette = ({ colors = {}, onChange }) => {
  const [newName, setNewName] = useState('');

  const handleChange = (name, val) => {
    onChange({ ...colors, [name]: val });
  };

  const handleDelete = (name) => {
    const next = { ...colors };
    delete next[name];
    onChange(next);
  };

  const handleAdd = () => {
    const trimmed = newName.trim().toLowerCase().replace(/\s+/g, '-');
    if (!trimmed || colors[trimmed] !== undefined) return;
    onChange({ ...colors, [trimmed]: '#CCCCCC' });
    setNewName('');
  };

  return (
    <Section title="Color Palette">
      {Object.entries(colors).map(([name, val]) => (
        <ColorRow
          key={name}
          name={name}
          value={val}
          onChange={handleChange}
          onDelete={handleDelete}
          deletable={!STANDARD_COLORS.includes(name)}
        />
      ))}
      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
        <TextField
          size="small"
          placeholder="custom-color"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          sx={{ flex: 1, '& .MuiInputBase-input': { fontSize: 12, py: 0.5 } }}
        />
        <IconButton
          size="small"
          onClick={handleAdd}
          disabled={!newName.trim()}
          sx={{ background: '#f5f5f5', borderRadius: 1, '&:hover': { background: '#eee' } }}
        >
          <AddIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>
    </Section>
  );
};

// ── Typography section ─────────────────────────────────────────────────────────

const TypographySection = ({ typography = {}, onChange }) => {
  const ff = typography.fontFamily || 'Inter';
  const base = typography.baseSizePx || 16;
  const scale = typography.scale || [12, 14, 16, 18, 22, 30, 40];

  return (
    <Section title="Typography">
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" sx={{ fontSize: 11, color: '#888', display: 'block', mb: 0.5 }}>
            Font Family
          </Typography>
          <TextField
            size="small"
            value={ff}
            onChange={(e) => onChange({ ...typography, fontFamily: e.target.value })}
            fullWidth
            sx={{ '& .MuiInputBase-input': { fontSize: 13, py: 0.5 } }}
          />
        </Box>
        <Box sx={{ width: 80 }}>
          <Typography variant="caption" sx={{ fontSize: 11, color: '#888', display: 'block', mb: 0.5 }}>
            Base (px)
          </Typography>
          <TextField
            size="small"
            type="number"
            value={base}
            onChange={(e) => onChange({ ...typography, baseSizePx: parseInt(e.target.value) || 16 })}
            sx={{ '& .MuiInputBase-input': { fontSize: 13, py: 0.5 } }}
          />
        </Box>
      </Box>
      {/* Type scale preview */}
      <Typography variant="caption" sx={{ fontSize: 11, color: '#888', display: 'block', mb: 1 }}>
        Type Scale Preview
      </Typography>
      <Box sx={{ background: '#fafafa', borderRadius: 1.5, p: 1.5, border: '1px solid #f0f0f0' }}>
        {scale.slice().sort((a, b) => b - a).map((size) => (
          <Box
            key={size}
            sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 0.5 }}
          >
            <Typography
              sx={{
                fontSize: size,
                fontFamily: ff,
                lineHeight: 1.2,
                color: '#1a1a1a',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              Aa
            </Typography>
            <Typography variant="caption" sx={{ fontSize: 10, color: '#aaa', flexShrink: 0 }}>
              {size}px
            </Typography>
          </Box>
        ))}
      </Box>
    </Section>
  );
};

// ── Spacing section ────────────────────────────────────────────────────────────

const SpacingSection = ({ spacing = {}, onChange }) => {
  const base = spacing.basePx || 8;
  const scale = spacing.scale || [4, 8, 12, 16, 24, 32, 48, 64];

  return (
    <Section title="Spacing">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Typography variant="caption" sx={{ fontSize: 11, color: '#888', flexShrink: 0 }}>
          Base unit (px)
        </Typography>
        <Slider
          value={base}
          min={2}
          max={16}
          step={1}
          onChange={(_, v) => onChange({ ...spacing, basePx: v })}
          sx={{ flex: 1, color: '#1a1a1a' }}
        />
        <Typography variant="body2" sx={{ fontSize: 12, width: 30, textAlign: 'right', fontFamily: 'monospace' }}>
          {base}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {scale.map((s) => (
          <Box key={s} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{
                width: s,
                height: 20,
                background: '#6366f1',
                borderRadius: 0.5,
                minWidth: 4,
              }}
            />
            <Typography variant="caption" sx={{ fontSize: 9, color: '#aaa' }}>{s}</Typography>
          </Box>
        ))}
      </Box>
    </Section>
  );
};

// ── Border Radius section ──────────────────────────────────────────────────────

const RadiiSection = ({ radii = {}, onChange }) => {
  const entries = [
    { key: 'sm', label: 'sm', default: 4 },
    { key: 'md', label: 'md', default: 8 },
    { key: 'lg', label: 'lg', default: 16 },
    { key: 'xl', label: 'xl', default: 24 },
  ];

  return (
    <Section title="Border Radius">
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {entries.map(({ key, label, default: def }) => {
          const val = radii[key] ?? def;
          return (
            <Box key={key} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, flex: '1 1 80px' }}>
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  background: '#f0f0f0',
                  border: '2px solid #ddd',
                  borderRadius: `${val}px`,
                }}
              />
              <Typography variant="caption" sx={{ fontSize: 11, color: '#888', textTransform: 'uppercase' }}>
                {label}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Slider
                  value={val}
                  min={0}
                  max={50}
                  step={1}
                  onChange={(_, v) => onChange({ ...radii, [key]: v })}
                  sx={{ width: 64, color: '#1a1a1a' }}
                  size="small"
                />
                <Typography variant="caption" sx={{ fontSize: 10, fontFamily: 'monospace', width: 24 }}>
                  {val}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Section>
  );
};

// ── TokenEditor ────────────────────────────────────────────────────────────────

/**
 * TokenEditor — visual editor for all design tokens.
 * Calls onSave(tokens) whenever any token changes (debounced).
 *
 * Props:
 *   tokens: { colors, typography, spacing, radii, shadows }
 *   onSave: (tokens) => void
 */
const TokenEditor = ({ tokens = {}, onSave }) => {
  const [localTokens, setLocalTokens] = useState(() => ({
    colors: {},
    typography: {},
    spacing: {},
    radii: {},
    shadows: {},
    ...tokens,
  }));

  const debouncedSave = useDebounce(onSave, 600);

  const update = (section, value) => {
    const next = { ...localTokens, [section]: value };
    setLocalTokens(next);
    debouncedSave(next);
  };

  return (
    <Box>
      <ColorPalette
        colors={localTokens.colors}
        onChange={(v) => update('colors', v)}
      />
      <Divider sx={{ my: 2 }} />
      <TypographySection
        typography={localTokens.typography}
        onChange={(v) => update('typography', v)}
      />
      <Divider sx={{ my: 2 }} />
      <SpacingSection
        spacing={localTokens.spacing}
        onChange={(v) => update('spacing', v)}
      />
      <Divider sx={{ my: 2 }} />
      <RadiiSection
        radii={localTokens.radii}
        onChange={(v) => update('radii', v)}
      />
    </Box>
  );
};

export default TokenEditor;
